/**
 * Global Error Reporter — captures errors and lets users send them to admin.
 *
 * Usage:
 *   import { reportError } from '../lib/errorReporter';
 *
 *   try { ... } catch (e) {
 *     reportError({
 *       error: e,
 *       page: '/pay-supplier',
 *       action: 'Pay PO from Safe',
 *       component: 'PaySupplierPage',
 *     });
 *   }
 *
 * This shows a toast: "An error occurred" with a "Send to Admin" button.
 * When clicked, the full error details are sent to POST /api/error-reports.
 */

import { toast } from 'sonner';
import { api } from '../contexts/AuthContext';

/**
 * Build a serializable error payload from a caught error.
 */
function buildPayload({ error, page, action, component }) {
  const e = error || {};

  // Extract axios-specific info
  const status = e.response?.status;
  const reqUrl = e.config?.url;
  const reqMethod = e.config?.method?.toUpperCase();

  // Build the raw message — prioritize detail from backend
  let errorMessage;
  const detail = e.response?.data?.detail;
  if (typeof detail === 'string') {
    errorMessage = detail;
  } else if (Array.isArray(detail) && detail.length) {
    errorMessage = detail.map(d => d.msg || String(d)).join('; ');
  } else if (detail && typeof detail === 'object' && detail.message) {
    errorMessage = detail.message;
  } else {
    errorMessage = e.message || String(e);
  }

  return {
    page: page || window.location.pathname,
    action: action || 'Unknown action',
    error_message: errorMessage,
    error_stack: e.stack?.slice(0, 2000) || null,
    component: component || null,
    request_url: reqUrl || null,
    request_method: reqMethod || null,
    http_status: status || null,
    browser_info: navigator.userAgent,
    extra: {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      response_data: e.response?.data ? JSON.stringify(e.response.data).slice(0, 500) : null,
    },
  };
}

/**
 * Show user-friendly error toast with "Send to Admin" button.
 */
export function reportError({ error, page, action, component, userMessage }) {
  const payload = buildPayload({ error, page, action, component });

  // Log to console for developer debugging
  console.error('[ErrorReporter]', payload);

  const displayMsg = userMessage || 'An error occurred while processing your request.';

  toast.error(displayMsg, {
    description: 'You can report this error so your admin can investigate.',
    duration: 12000,
    action: {
      label: 'Send to Admin',
      onClick: async () => {
        try {
          const res = await api.post('/error-reports', payload);
          const code = res.data?.error_code;
          toast.success(
            code
              ? `Error ${code} reported — your admin will review it.`
              : 'Error reported to admin.',
          );
        } catch {
          toast.error('Could not send error report. Please contact your admin directly.');
        }
      },
    },
  });
}
